import * as THREE from 'three';

const HAND_SWING_DURATION = 0.17;

function finalizeHeldMesh(obj) {
  obj.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = false;
    node.receiveShadow = false;
    node.frustumCulled = false;
  });
  return obj;
}

/* ── arm mesh (skin-coloured forearm + fist) ──────────────────────── */

function buildArmMesh() {
  const group = new THREE.Group();
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xc69c6d, roughness: 0.82, metalness: 0.02 });
  const sleeveMat = new THREE.MeshStandardMaterial({ color: 0x5a7a4c, roughness: 0.88, metalness: 0.01 });

  // Sleeve (upper part of visible arm)
  const sleeve = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.18, 0.11), sleeveMat);
  sleeve.position.set(0, 0.08, 0);
  group.add(sleeve);

  // Forearm (skin)
  const forearm = new THREE.Mesh(new THREE.BoxGeometry(0.10, 0.28, 0.10), skinMat);
  forearm.position.set(0, -0.12, 0);
  group.add(forearm);

  // Fist
  const fist = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.09, 0.12), skinMat);
  fist.position.set(0, -0.28, 0.01);
  group.add(fist);

  return finalizeHeldMesh(group);
}

/* ── tool mesh builders (unchanged) ───────────────────────────────── */

function buildHeldToolMesh(def) {
  const group = new THREE.Group();
  const handleMat = new THREE.MeshStandardMaterial({ color: 0x8f6f44, roughness: 0.8, metalness: 0.02 });
  const headMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(def.color), roughness: 0.45, metalness: 0.25 });

  const needsHandle = ['pickaxe', 'axe', 'shovel', 'hoe', 'sword'].includes(def.toolType);
  if (needsHandle) {
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.8, 8), handleMat);
    handle.rotation.z = Math.PI / 2;
    group.add(handle);
  }

  if (def.toolType === 'pickaxe') {
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.075, 0.07), headMat);
    head.position.set(0.17, 0.02, 0);
    group.add(head);

    const spikeL = new THREE.Mesh(new THREE.ConeGeometry(0.034, 0.12, 6), headMat);
    spikeL.position.set(0.01, 0.02, 0);
    spikeL.rotation.z = Math.PI * 0.5;
    group.add(spikeL);

    const spikeR = spikeL.clone();
    spikeR.position.set(0.33, 0.02, 0);
    spikeR.rotation.z = -Math.PI * 0.5;
    group.add(spikeR);
  } else if (def.toolType === 'axe') {
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.2, 0.07), headMat);
    head.position.set(0.14, 0.08, 0);
    group.add(head);

    const blade = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.22, 4), headMat);
    blade.position.set(0.18, 0.08, 0.01);
    blade.rotation.z = Math.PI / 2;
    group.add(blade);
  } else if (def.toolType === 'sword') {
    const cross = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.045, 0.06), headMat);
    cross.position.set(0.12, 0.06, 0);
    group.add(cross);

    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.07, 0.05), headMat);
    blade.position.set(0.42, 0.06, 0);
    group.add(blade);

    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.11, 5), headMat);
    tip.position.set(0.72, 0.06, 0);
    tip.rotation.z = -Math.PI / 2;
    group.add(tip);
  } else if (def.toolType === 'bow') {
    const arcMat = new THREE.MeshStandardMaterial({ color: 0xb59064, roughness: 0.82, metalness: 0.02 });
    const upper = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.44, 8), arcMat);
    upper.position.set(0.14, 0.16, 0);
    upper.rotation.z = Math.PI / 4.2;
    group.add(upper);

    const lower = upper.clone();
    lower.position.set(0.14, -0.16, 0);
    lower.rotation.z = -Math.PI / 4.2;
    group.add(lower);

    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.22, 0.06), headMat);
    grip.position.set(0.04, 0, 0);
    group.add(grip);

    const string = new THREE.Mesh(
      new THREE.CylinderGeometry(0.007, 0.007, 0.64, 6),
      new THREE.MeshStandardMaterial({ color: 0xded6c5, roughness: 0.92, metalness: 0.0 })
    );
    string.position.set(0.34, 0, 0);
    string.rotation.z = Math.PI / 2;
    group.add(string);
  } else if (def.toolType === 'shield') {
    const frontMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(def.color), roughness: 0.78, metalness: 0.08 });
    const face = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.66, 0.1), frontMat);
    face.position.set(0.18, 0, 0);
    group.add(face);

    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(0.54, 0.72, 0.03),
      new THREE.MeshStandardMaterial({ color: 0xd3c2a6, roughness: 0.62, metalness: 0.14 })
    );
    trim.position.set(0.18, 0, 0.06);
    group.add(trim);

    const grip = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.22, 0.09),
      new THREE.MeshStandardMaterial({ color: 0x8f6f44, roughness: 0.84, metalness: 0.02 })
    );
    grip.position.set(0.04, 0, 0.02);
    group.add(grip);
  } else if (def.toolType === 'bucket') {
    const bucketMat = new THREE.MeshStandardMaterial({ color: 0x8a939e, roughness: 0.75, metalness: 0.35 });
    const bucketBody = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.09, 0.22, 12), bucketMat);
    bucketBody.position.set(0.25, 0.08, 0);
    const innerBucket = new THREE.Mesh(new THREE.CylinderGeometry(0.10, 0.07, 0.221, 12), new THREE.MeshBasicMaterial({ color: 0x222222 }));
    innerBucket.position.set(0.25, 0.08, 0);
    group.add(bucketBody);
    group.add(innerBucket);

    if (def.id === 'water_bucket' || def.id === 'lava_bucket') {
      const fluidColor = def.id === 'water_bucket' ? 0x3f84dd : 0xf06722;
      const emissive = def.id === 'lava_bucket' ? 0xff8c35 : 0x000000;
      const fluidMat = new THREE.MeshStandardMaterial({ color: fluidColor, emissive: emissive, roughness: 0.1 });
      const fluid = new THREE.Mesh(new THREE.CylinderGeometry(0.10, 0.08, 0.2, 12), fluidMat);
      fluid.position.set(0.25, 0.09, 0);
      group.add(fluid);
    }
  } else if (def.toolType === 'hoe') {
    const neck = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.05, 0.05), headMat);
    neck.position.set(0.16, 0.02, 0);
    group.add(neck);

    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.18, 0.05), headMat);
    blade.position.set(0.2, -0.04, 0);
    group.add(blade);
  } else {
    const genericHead = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.08, 0.08), headMat);
    genericHead.position.set(0.2, 0.04, 0);
    group.add(genericHead);
  }

  group.scale.setScalar(0.95);
  group.rotation.set(0.05, 0.2, 0.18);
  return finalizeHeldMesh(group);
}

function buildHeldItemMesh(itemId, itemDefs) {
  const def = itemDefs[itemId];
  if (!def) return null;

  if (def.kind === 'tool') return buildHeldToolMesh(def);

  const color = new THREE.Color(def.color);
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.6,
    metalness: def.kind === 'material' ? 0.25 : 0.05,
    emissive: itemId === 'torch' ? 0xffab44 : 0x000000,
    emissiveIntensity: itemId === 'torch' ? 0.55 : 0
  });

  if (itemId === 'stick') {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.022, 0.52, 8), mat);
    mesh.rotation.z = Math.PI / 2;
    return finalizeHeldMesh(mesh);
  }

  if (itemId === 'string') {
    const group = new THREE.Group();
    for (let i = 0; i < 3; i++) {
      const thread = new THREE.Mesh(
        new THREE.CylinderGeometry(0.006, 0.006, 0.46, 6),
        new THREE.MeshStandardMaterial({ color: 0xdcd4c7, roughness: 0.93, metalness: 0.0 })
      );
      thread.position.set((i - 1) * 0.03, 0, 0);
      thread.rotation.z = Math.PI / 2 + (i - 1) * 0.04;
      group.add(thread);
    }
    return finalizeHeldMesh(group);
  }

  if (itemId === 'arrow') {
    const group = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.013, 0.013, 0.62, 8), mat);
    shaft.rotation.z = Math.PI / 2;
    group.add(shaft);

    const head = new THREE.Mesh(
      new THREE.ConeGeometry(0.033, 0.09, 4),
      new THREE.MeshStandardMaterial({ color: 0xb8b8b8, roughness: 0.52, metalness: 0.16 })
    );
    head.position.set(0.35, 0, 0);
    head.rotation.z = -Math.PI / 2;
    group.add(head);

    return finalizeHeldMesh(group);
  }

  if (itemId === 'torch') {
    const group = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.35, 0.04), new THREE.MeshStandardMaterial({ color: 0x8f6f44, roughness: 0.9 }));
    body.position.set(0.2, 0.15, 0);
    group.add(body);

    const flameMat = new THREE.MeshStandardMaterial({ color: 0xffcc33, emissive: 0xff9900, emissiveIntensity: 1.5, roughness: 0.2, metalness: 0 });
    const flame = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.08, 0.05), flameMat);
    flame.position.set(0.2, 0.35, 0);
    group.add(flame);

    return finalizeHeldMesh(group);
  }

  if (itemId === 'coal') {
    return finalizeHeldMesh(new THREE.Mesh(new THREE.DodecahedronGeometry(0.11), mat));
  }

  if (itemId === 'diamond_gem') {
    const mesh = new THREE.Mesh(new THREE.OctahedronGeometry(0.13), mat);
    mesh.rotation.set(0.2, 0.4, 0.1);
    return finalizeHeldMesh(mesh);
  }

  if (itemId === 'iron_ingot') {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.1, 0.16), mat);
    mesh.rotation.set(0.12, 0.26, 0.04);
    return finalizeHeldMesh(mesh);
  }

  const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.28, 0.28), mat);
  mesh.rotation.set(0.04, 0.24, 0.03);
  return finalizeHeldMesh(mesh);
}

/* ── main system ──────────────────────────────────────────────────── */

export function createHeldItemSystem({ camera, controls, ui, itemDefs }) {
  /* ── right hand (holds active item) ───────────────────────────── */
  const heldItemRoot = new THREE.Group();
  heldItemRoot.position.set(0.42, -0.38, -0.64);
  heldItemRoot.rotation.set(-0.32, -0.58, -0.08);
  heldItemRoot.visible = false;
  camera.add(heldItemRoot);

  // Right-hand arm behind the held item
  const rightArm = buildArmMesh();
  rightArm.position.set(-0.05, -0.18, 0.06);
  rightArm.rotation.set(0.1, 0, 0.08);
  heldItemRoot.add(rightArm);

  /* ── left hand (bare arm, always visible in first-person) ─────── */
  const leftHandRoot = new THREE.Group();
  leftHandRoot.position.set(-0.48, -0.42, -0.64);
  leftHandRoot.rotation.set(-0.32, 0.58, 0.08);
  leftHandRoot.visible = false;
  camera.add(leftHandRoot);

  const leftArm = buildArmMesh();
  leftArm.position.set(0.05, -0.14, 0.04);
  leftArm.rotation.set(0.08, 0, -0.06);
  leftHandRoot.add(leftArm);

  let heldItemMesh = null;
  let heldItemId = null;
  let heldSwingTime = 0;
  let heldSwingStrength = 0;

  function clear() {
    heldSwingTime = 0;
    heldSwingStrength = 0;
    heldItemRoot.visible = false;
    leftHandRoot.visible = false;
  }

  function triggerSwing(strength = 1) {
    const swingStrength = THREE.MathUtils.clamp(strength, 0.25, 1.9);
    heldSwingTime = HAND_SWING_DURATION;
    heldSwingStrength = Math.max(heldSwingStrength * 0.58, swingStrength);
  }

  function update(time, delta = 0.016) {
    heldSwingTime = Math.max(0, heldSwingTime - delta);
    if (heldSwingTime <= 0) {
      heldSwingStrength = Math.max(0, heldSwingStrength - delta * 3.2);
    }

    const shouldShow = controls.isLocked && !ui.isCraftOpen();

    /* ── left hand: always visible when in first-person ─────────── */
    leftHandRoot.visible = shouldShow;
    if (shouldShow) {
      const ts = time * 0.0014;
      // Phase-offset idle bob (π/2 out of phase with right hand)
      const leftSin = Math.sin(ts * 1.8 + Math.PI * 0.5);
      const leftAbs = Math.abs(leftSin);
      leftHandRoot.position.x = -0.48 + leftSin * 0.007;
      leftHandRoot.position.y = -0.42 + leftAbs * 0.007;
      leftHandRoot.position.z = -0.64;
      leftHandRoot.rotation.x = -0.32;
      leftHandRoot.rotation.y = 0.58 + leftSin * 0.014;
      leftHandRoot.rotation.z = 0.08 + leftSin * 0.016;
    }

    /* ── right hand: shows held item ────────────────────────────── */
    if (!shouldShow) {
      heldItemRoot.visible = false;
      return;
    }

    const activeItemId = ui.getActiveItemId();
    if (!activeItemId || ui.getActiveItemCount() <= 0) {
      heldItemRoot.visible = false;
      heldItemId = null;
      if (heldItemMesh) {
        heldItemRoot.remove(heldItemMesh);
        heldItemMesh = null;
      }
      return;
    }

    if (heldItemId !== activeItemId) {
      if (heldItemMesh) heldItemRoot.remove(heldItemMesh);
      heldItemMesh = buildHeldItemMesh(activeItemId, itemDefs);
      heldItemId = activeItemId;
      if (heldItemMesh) heldItemRoot.add(heldItemMesh);
    }

    heldItemRoot.visible = !!heldItemMesh;
    if (!heldItemMesh) return;

    const timeScale = time * 0.0014;
    const idleSin = Math.sin(timeScale * 1.8);
    const idleAbs = Math.abs(idleSin);
    const swingProgress = heldSwingTime > 0 ? 1 - (heldSwingTime / HAND_SWING_DURATION) : 1;
    const swingCurve = heldSwingTime > 0 ? Math.sin(swingProgress * Math.PI) : 0;
    const hitCurve = heldSwingTime > 0 ? Math.pow(Math.sin(swingProgress * Math.PI), 1.25) : 0;
    const swing = swingCurve * Math.max(0.24, heldSwingStrength);

    heldItemRoot.position.x = 0.42 + idleSin * 0.008 + swing * 0.052;
    heldItemRoot.position.y = -0.38 + idleAbs * 0.008 - hitCurve * 0.125;
    heldItemRoot.position.z = -0.64 - hitCurve * 0.16;
    heldItemRoot.rotation.x = -0.32 - hitCurve * 0.95;
    heldItemRoot.rotation.y = -0.58 + swing * 0.15;
    heldItemRoot.rotation.z = -0.08 + idleSin * 0.018 + swing * 0.26;
  }

  return {
    clear,
    triggerSwing,
    update
  };
}
